import { ButtonOptions } from '@usitsdasdesign/dds-ng/button';
import { DropdownOptions } from '@usitsdasdesign/dds-ng/dropdown';
import {
  ButtonKind,
  ErrorState,
  ExtThemes,
  LabelPosition,
  PositionState,
  Size,
  Themes,
  WidthState,
} from '@usitsdasdesign/dds-ng/shared';
import { TextareaOptions } from '@usitsdasdesign/dds-ng/textarea';
import { ToastOptions } from '@usitsdasdesign/dds-ng/toast';
import { TooltipOptions } from '@usitsdasdesign/dds-ng/tooltip';

export class SharedConstant {
  public static readonly DEFAULT_PAGE_NUMBER: number = 1;
  public static readonly PAGE_SIZE: number = 5;
  public static readonly PAGE_SIZE_10: number = 10;
  public static readonly PAGE_SIZE_15: number = 15;
  public static readonly TEXTAREA_MIN_HEIGHT: number = 70;
  public static readonly TOOLTIP_WIDTH_450 = 450;
  public static readonly SEARCH_DEBOUNCE_TIME: number = 500; //.5s
  public static readonly TIMEOUT_DURATION_1 = 1000; // 1s
  public static readonly TIMEOUT_DURATION_DEFAULT = 2000; //2s
  public static readonly TIMEOUT_TIMER: number = 2500; // 2.5s
  public static readonly TIMEOUT_DURATION_3 = 3000; // 3s
  public static readonly TEXT_MIN_LENGTH_2: number = 2;
  public static readonly TEXT_MIN_LENGTH_3: number = 3;
  public static readonly TEXT_MAX_LENGTH_11: number = 11;
  public static readonly TEXT_MAX_LENGTH_100: number = 100;
  public static readonly TEXT_MAX_LENGTH_1000: number = 1000;
  public static readonly TEXT_MAX_LENGTH_5000: number = 5000;
  public static readonly longOldDate: number = -5000000000000;
  public static readonly longNewDate: number = 5000000000000;
  public static readonly notAvailable: string = 'Not Available';

  public static readonly EMPTY_STRING: string = '';
  public static readonly DEFAULT_FOR_EMPTY_FIELDS: string = '-';
  public static readonly UI_ELEMENT_SIZE_LG: Size = Size.lg;
  public static readonly MODAL_SIZE_MD: Size = Size.md;
  public static readonly THEME_GREEN: Themes = Themes.green; // Default theme
  public static readonly THEME_DARK: Themes = Themes.dark;
  public static readonly EXT_THEME_GREEN: ExtThemes = ExtThemes.green;
  public static readonly EXT_THEME_DARK: ExtThemes = ExtThemes.dark;
  public static readonly ERROR_DEFAULT: ErrorState = ErrorState.default;

  // Date Format
  public static readonly DATE_FORMAT: string = 'dd/MM/yyyy';
  public static readonly DATE_FORMAT_US: string = 'MM/dd/yyyy';
  public static readonly DATESTRING_FORMAT_US: string = 'en-US';

  // Labels
  public static readonly SEARCH_OPTION_PLACEHOLDER_SIMPLE: string = 'Search';
  public static readonly CHANGE_ORDER_LABEL: string = 'Change Order';
  public static readonly DELIVERABLE: string = 'Deliverable';
  public static readonly ASCENDING: string = 'ascending';
  public static readonly DECENDING: string = 'decending';
  public static readonly YES_CAPITALCASE: string = 'Yes';
  public static readonly NO_CAPITALCASE: string = 'No';
  public static readonly YES_LOWERCASE: string = 'yes';
  public static readonly NO_LOWERCASE: string = 'no';
  public static readonly NOT_YET_DETERMINED: string = 'Not yet determined';
  public static readonly NONE: string = 'None';
  public static readonly NONE_EXPLAIN: string = 'None [Explain]'; // Used in single file
  public static readonly ENTER_TEXT_PLACEHOLDER: string = 'Enter text';
  public static readonly NO_MATCHES_FOUND: string = 'No matches found';
  public static readonly SHOW_MORE_TEXT: string = 'Show more';
  public static readonly SHOW_LESS_TEXT: string = 'Show less';
  public static readonly COPY_SUCCESS_MSG: string = 'Copied to clipboard!';
  public static readonly DESCRIPTION_LABEL: string = 'Description';

  // Icons
  public static readonly DDS_ICON_ARROW_UP: string = 'dds-icon_arrow-up';
  public static readonly DDS_ICON_ARROW_DOWN: string = 'dds-icon_arrow-down';
  public static readonly DDS_ICON_CLOSE: string = 'dds-icon_close';
  public static readonly DDS_ICON_PLUS: string = 'dds-icon_plus';
  public static readonly DDS_ICON_COPY: string = 'dds-icon_copy__l__stroke';
  public static readonly DDS_ICON_EDIT: string = 'dds-icon_edit__s__filled';

  // Mapper
  public static readonly YES_NO_CHECKBOX_VALUE_TO_BOOLEAN = new Map<
    string | boolean,
    boolean
  >([
    ['yes', true],
    ['no', false],
  ]);
  public static readonly BOOLEAN_CHECKBOX_VALUE_TO_YES_NO = new Map<
    boolean | undefined | string,
    string
  >([
    [true, 'yes'],
    [false, 'no'],
  ]);

  // Dashboard
  public static readonly REVIEWER_DASHBOARD: string = 'ReviewerDashboard';
  public static readonly ENGAGEMENT_TEAM_DASHBOARD: string =
    'EngagementTeamDashboard';
  public static readonly RISK_MANAGER_INSIGHT_DASHBOARD: string =
    'RiskManagerInsightDashboard';
}

// DDS Component Config
export const DarkTooltipOptions: TooltipOptions = {
  tooltipInvokeType: 'hover',
  tooltipPosition: 'top',
  tooltipIndent: 15,
  tooltipHasBeak: true,
  tooltipType: 'regular',
  tooltipSize: 'md',
  tooltipTheme: SharedConstant.THEME_DARK,
  tooltipMaxWidth: 300,
};

// **** DDS Form Config ****
export const TextareaBaseOptions: TextareaOptions = {
  label: SharedConstant.EMPTY_STRING,
  labelPosition: LabelPosition.external,
  placeholder: SharedConstant.ENTER_TEXT_PLACEHOLDER,
  minHeight: SharedConstant.TEXTAREA_MIN_HEIGHT,
  isRequired: false,
  isInverse: false,
  readonly: false,
  isError: false,
  errorState: ErrorState.default,
  description: SharedConstant.EMPTY_STRING,
  customClass: SharedConstant.EMPTY_STRING,
  errorMessage: SharedConstant.EMPTY_STRING,
  maxLength: SharedConstant.TEXT_MAX_LENGTH_1000,
  maxLengthPosition: PositionState.bottomRight,
  size: SharedConstant.UI_ELEMENT_SIZE_LG,
};

// **** DDS Button Config ****
export const ButtonsBaseOption: ButtonOptions = {
  size: SharedConstant.UI_ELEMENT_SIZE_LG,
  width: WidthState.fixed,
  isLoading: false,
  isIconLeft: false,
  isColorBg: false,
  isInverse: false,
  disabled: false,
  ariaLabel: 'Button',
  customClass: SharedConstant.EMPTY_STRING,
  role: 'button',
};

export const PrimaryButtonBaseOption: ButtonOptions = {
  ...ButtonsBaseOption,
  theme: SharedConstant.EXT_THEME_GREEN,
  kind: ButtonKind.primary,
};

export const SecondaryButtonBaseOption: ButtonOptions = {
  ...ButtonsBaseOption,
  theme: SharedConstant.EXT_THEME_GREEN,
  kind: ButtonKind.secondary,
};

export const PrimaryLoudButtonBaseOption: ButtonOptions = {
  ...ButtonsBaseOption,
  theme: SharedConstant.EXT_THEME_GREEN,
  kind: ButtonKind.primaryLoud,
};

export const SecondaryLoudButtonBaseOption: ButtonOptions = {
  ...ButtonsBaseOption,
  theme: SharedConstant.EXT_THEME_GREEN,
  kind: ButtonKind.secondaryLoud,
};

export const SilentButtonBaseOption: ButtonOptions = {
  ...ButtonsBaseOption,
  theme: SharedConstant.EXT_THEME_GREEN,
  kind: ButtonKind.silent,
};

export const ModalCloseBtnOptions: ButtonOptions = {
  ...SilentButtonBaseOption,
  theme: SharedConstant.EXT_THEME_DARK,
  icon: SharedConstant.DDS_ICON_CLOSE,
  ariaLabel: 'Close modal',
};

export const ShowMoreBtnOptions: ButtonOptions = {
  theme: SharedConstant.EXT_THEME_GREEN,
  kind: ButtonKind.silent,
  size: SharedConstant.UI_ELEMENT_SIZE_LG,
  icon: SharedConstant.DDS_ICON_ARROW_DOWN,
  ariaLabel: SharedConstant.SHOW_MORE_TEXT,
};

// **** DDS Toast Config ****
export const ToastBaseOptions: ToastOptions = {
  theme: SharedConstant.THEME_DARK,
  lifeTime: 3000,
  withProgressBar: false,
  position: 'bottom-center',
  isCloseIcon: false,
  customClass: 'toast-align simple-toast',
  isError: false,
  isInverse: false,
  isLoading: false,
  limit: 5,
};
